import React, { useState, useEffect } from 'react';
import {
  FaSave,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaCloudUploadAlt,
  FaSpinner,
  FaTimes,
  FaCheckCircle,
  FaEnvelope,
  FaMapMarkerAlt,
  FaGraduationCap,
  FaBriefcase,
  FaExclamationCircle,
} from 'react-icons/fa';
import { useAdmin } from '../context/AdminContext';
import { MEDIA_BASE_URL } from '../../config';
import './SectionManager.css';
import './TeamManager.css';

// 🎯 PURE JS CANVAS IMAGE COMPRESSOR
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    if (file.size <= 500 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '') + '.jpg',
              {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }
            );
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const TeamManager = () => {
  const { data, addItem, updateItem, deleteItem, loading } = useAdmin();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    role: '',
    department: 'Leadership',
    email: '',
    experience: '',
    education: '',
    location: '',
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [sizeInfo, setSizeInfo] = useState({ original: '', compressed: '' });
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const teamList = data?.team || [];

  const getCleanPreviewUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http') || img.startsWith('blob:')) return img;
    return `${MEDIA_BASE_URL}${img.startsWith('/') ? '' : '/'}${img}`;
  };

  const notifySuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  // 🎯 Auto-Compress Profile Image on Select
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg('Please select a valid image file!');
      return;
    }

    try {
      setIsCompressing(true);
      setErrorMsg('');
      const originalSizeFormatted = formatFileSize(file.size);

      // Compress image
      const compressed = await compressImage(file);
      const compressedSizeFormatted = formatFileSize(compressed.size);

      setImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
      setSizeInfo({
        original: originalSizeFormatted,
        compressed: compressedSizeFormatted,
      });
    } catch (err) {
      console.error('Compression Error:', err);
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!form.name.trim() || !form.role.trim()) {
      setErrorMsg('Full Name and Role/Position are required fields.');
      return;
    }

    try {
      const payload = { ...form, imageFile };
      if (editId) {
        await updateItem('team', editId, payload);
        notifySuccess('Board member profile updated successfully!');
      } else {
        await addItem('team', payload);
        notifySuccess('New board member registered successfully!');
      }
      resetForm();
    } catch (err) {
      console.error('Error saving team member:', err);
      setErrorMsg('Failed to save profile. Please try again.');
    }
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '',
      role: item.role || '',
      department: item.department || 'Leadership',
      email: item.email || '',
      experience: item.experience || '',
      education: item.education || '',
      location: item.location || '',
    });

    const activeImg = item.emoji || item.avatar_icon || '';
    setImagePreview(getCleanPreviewUrl(activeImg));
    setImageFile(null);
    setSizeInfo({ original: '', compressed: '' });
    setEditId(item.id);
    setShowForm(true);
    setErrorMsg('');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to retire this board member profile?')) {
      await deleteItem('team', id);
      notifySuccess('Board member profile retired successfully.');
    }
  };

  const removeSelectedImage = () => {
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    const input = document.getElementById('team-photo-input');
    if (input) input.value = '';
  };

  const resetForm = () => {
    setForm({
      name: '',
      role: '',
      department: 'Leadership',
      email: '',
      experience: '',
      education: '',
      location: '',
    });
    setImageFile(null);
    setImagePreview('');
    setSizeInfo({ original: '', compressed: '' });
    setIsCompressing(false);
    setEditId(null);
    setShowForm(false);
    setErrorMsg('');
  };

  const getDepartmentBadgeClass = (dept = '') => {
    const d = dept.toLowerCase();
    if (d.includes('leadership') || d.includes('executive')) return 'sg-tag-leadership';
    if (d.includes('education')) return 'sg-tag-education';
    if (d.includes('medical')) return 'sg-tag-medical';
    if (d.includes('construction')) return 'sg-tag-construction';
    if (d.includes('marketing')) return 'sg-tag-marketing';
    return 'sg-tag-support';
  };

  if (loading && teamList.length === 0) {
    return (
      <div className="sg-manager">
        <div className="tm-loader">
          <FaSpinner className="spin" />
          <p>Loading board profiles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sg-manager">
      {/* ═══════════════════════════════════════════
          PAGE HEADER
         ═══════════════════════════════════════════ */}
      <div className="sg-page-header">
        <div className="sg-ph-left">
          <div className="sg-ph-icon">
            <FaUsers />
          </div>
          <div className="sg-ph-text">
            <h2>Leadership Team</h2>
            <p>Appoint Board Directors, modify executive bios, and control leadership profiles</p>
          </div>
        </div>
        <div className="sg-ph-right">
          <button className="sg-btn-gold" onClick={() => setShowForm(!showForm)}>
            {showForm ? (
              <>
                <FaTimes /> Close Form
              </>
            ) : (
              <>
                <FaPlus /> Appoint Director
              </>
            )}
          </button>
        </div>
      </div>

      {/* Action Alerts */}
      {successMsg && (
        <div className="sg-alert sg-alert-success">
          <FaCheckCircle /> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="sg-alert sg-alert-error">
          <FaExclamationCircle /> {errorMsg}
        </div>
      )}

      {/* ═══════════════════════════════════════════
          FORM CONTAINER
         ═══════════════════════════════════════════ */}
      {showForm && (
        <div className="sg-form-card">
          <div className="sg-form-header">
            <div>
              <h3>{editId ? 'Modify Executive Profile' : 'Appoint New Board Director'}</h3>
              <p>Enter official qualifications and register corporate portfolio</p>
            </div>
            <button className="sg-form-close" onClick={resetForm}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="sg-form">
            <div className="sg-form-row">
              {/* Name */}
              <div className="sg-field">
                <label>
                  Full Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Mr. Rajesh Mehta"
                  required
                />
              </div>

              {/* Role */}
              <div className="sg-field">
                <label>
                  Role/Position <span className="req">*</span>
                </label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="e.g. Founder & MD"
                  required
                />
              </div>
            </div>

            <div className="sg-form-row-3">
              {/* Department */}
              <div className="sg-field">
                <label>Department / Board Sector</label>
                <select
                  value={form.department}
                  onChange={(e) => setForm({ ...form, department: e.target.value })}
                >
                  <option value="Leadership">Leadership Board</option>
                  <option value="Education">Education Sector</option>
                  <option value="Medical">Medical Sector</option>
                  <option value="Construction">Construction Sector</option>
                  <option value="Marketing">Marketing Board</option>
                  <option value="HR & Support">HR & Support</option>
                  <option value="Management">Management Board</option>
                  <option value="Operations">Operations Board</option>
                </select>
              </div>

              {/* Experience */}
              <div className="sg-field">
                <label>
                  <FaBriefcase className="tm-field-icon" /> Years of Experience
                </label>
                <input
                  type="text"
                  value={form.experience}
                  onChange={(e) => setForm({ ...form, experience: e.target.value })}
                  placeholder="e.g. 20+ Years"
                />
              </div>

              {/* Compressed Photo Field */}
              <div className="sg-field">
                <label>Executive Profile Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  id="team-photo-input"
                  disabled={isCompressing}
                />
                <label
                  htmlFor="team-photo-input"
                  className={`tm-file-label ${isCompressing ? 'disabled' : ''}`}
                >
                  {isCompressing ? (
                    <>
                      <FaSpinner className="spin" /> Compressing Profile...
                    </>
                  ) : (
                    <>
                      <FaCloudUploadAlt /> Choose Portrait File
                    </>
                  )}
                </label>
              </div>
            </div>

            {/* Profile Photo Preview Bubble & Compression Savings */}
            {imagePreview && (
              <div className="tm-preview-row">
                <div className="tm-avatar-frame">
                  <img src={imagePreview} alt="Portrait Preview" />
                  <button type="button" className="tm-avatar-remove" onClick={removeSelectedImage}>
                    <FaTimes />
                  </button>
                </div>
                {sizeInfo.compressed && (
                  <div className="tm-size-note">
                    <FaCheckCircle />
                    <div>
                      <strong>Portrait Optimized</strong>
                      <span>
                        Original: <del>{sizeInfo.original}</del> ➔{' '}
                        <strong>{sizeInfo.compressed}</strong>
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="sg-form-row-3">
              {/* Email */}
              <div className="sg-field">
                <label>
                  <FaEnvelope className="tm-field-icon" /> Direct Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="e.g. md@sarwagroup.com"
                />
              </div>

              {/* Education */}
              <div className="sg-field">
                <label>
                  <FaGraduationCap className="tm-field-icon" /> Academic Education
                </label>
                <input
                  type="text"
                  value={form.education}
                  onChange={(e) => setForm({ ...form, education: e.target.value })}
                  placeholder="e.g. MBA (Oxford) / B.Tech"
                />
              </div>

              {/* Location */}
              <div className="sg-field">
                <label>
                  <FaMapMarkerAlt className="tm-field-icon" /> Office Headquarters
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. HQ, Raipur CG"
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="sg-form-actions">
              <button type="submit" className="sg-btn-gold" disabled={isCompressing}>
                <FaSave /> {editId ? 'Save Profile' : 'Confirm Appointment'}
              </button>
              <button
                type="button"
                className="sg-btn-outline"
                onClick={resetForm}
                disabled={isCompressing}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ═══════════════════════════════════════════
          DATA VIEW (TABLE LIST)
         ═══════════════════════════════════════════ */}
      <div className="sg-table-card">
        <div className="sg-table-header">
          <div>
            <h3>Active Leadership Board</h3>
            <p>Directorial profiles active across Sarwa Group corporate subsidiaries</p>
          </div>
          <span className="sg-count-badge">{teamList.length} Active Directors</span>
        </div>

        <div className="sg-table-wrap">
          <table className="sg-table">
            <thead>
              <tr>
                <th style={{ width: '90px' }}>Portrait</th>
                <th>Director Name</th>
                <th>Official Position</th>
                <th style={{ width: '180px' }}>Board Sector</th>
                <th>Executive Email</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamList.length === 0 ? (
                <tr>
                  <td colSpan="6" className="sg-empty">
                    <div className="sg-empty-inner">
                      <FaUsers />
                      <h4>No Board Members Appointed</h4>
                      <p>Click "Appoint Director" above to register your first executive bio</p>
                    </div>
                  </td>
                </tr>
              ) : (
                teamList.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="tm-table-avatar-frame">
                        {item.emoji || item.avatar_icon ? (
                          <img
                            src={getCleanPreviewUrl(item.emoji || item.avatar_icon)}
                            alt={item.name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100';
                            }}
                          />
                        ) : (
                          <div className="tm-table-avatar-placeholder">
                            <span>{(item.name || 'T').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <strong className="sg-row-title">{item.name || 'Retired Profile'}</strong>
                    </td>
                    <td>
                      <span className="tm-row-role">{item.role || 'Board Member'}</span>
                    </td>
                    <td>
                      <span className={`sg-tag ${getDepartmentBadgeClass(item.department)}`}>
                        {item.department || 'Leadership'}
                      </span>
                    </td>
                    <td>
                      <span className="tm-row-email">
                        <FaEnvelope /> {item.email || 'hq@sarwagroup.com'}
                      </span>
                    </td>
                    <td>
                      <div className="sg-actions">
                        <button
                          className="sg-action-btn sg-edit"
                          onClick={() => handleEdit(item)}
                          title="Modify Profile"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="sg-action-btn sg-delete"
                          onClick={() => handleDelete(item.id)}
                          title="Retire Profile"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeamManager;